import { debounce } from '@ember/runloop';

import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';

import type VisualizationFunnel from 'webapp/models-custom/visualization/funnel';
import type VisualizationPipeline from 'webapp/models-custom/visualization/pipeline';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import type DChartSeries from 'webapp/d-chart/series/series';

// a rendered HTML label, with the Highcharts internals read when placing it (alignAttr, width, height)
export type RenderedLabel = Highcharts.SVGElement & {
	element: HTMLElement;
	alignAttr: Highcharts.SVGAttributes;
	width: number;
	height: number;
};

// a plot line (a step's start) or band (a step), read when placing the labels on render
export type PlotLineOrBandWithLabel = Omit<Highcharts.PlotLineOrBand, 'label' | 'options'> & {
	label?: RenderedLabel;
	options: Highcharts.AxisPlotBandsOptions & Highcharts.AxisPlotLinesOptions;
};

// Highcharts internals read when placing the labels on render
export type AxisWithPlotLinesAndBands = Highcharts.Axis & {
	plotLinesAndBands: PlotLineOrBandWithLabel[];
};

export type PointWithDataLabel = Highcharts.Point & {
	dataLabel?: RenderedLabel;
	custom: { plotLineId: number };
	yBottom: number;
	plotY: number;
	stackY: number;
};

// a series as built here; its type comes from the chart options (areaspline / streamgraph)
type FunnelOrPipelineSeriesOptions = Omit<
	Highcharts.SeriesAreasplineOptions,
	'type' | 'data' | 'colorIndex'
> & {
	title: string; // not a Highcharts option
	data: DChartInterface.ChartDataPoint[];
	colorIndex?: string | number; // a class for custom colors
	dbxSeries: DChartSeries;
};

// dataOptions with its axes as arrays
type FunnelOrPipelineOptions = Omit<Highcharts.Options, 'series' | 'xAxis' | 'yAxis' | 'legend'> & {
	series: FunnelOrPipelineSeriesOptions[];
	xAxis: Highcharts.XAxisOptions[];
	yAxis?: Highcharts.YAxisOptions[];
	legend?: Highcharts.LegendOptions;
};

export default abstract class FunnelOrPipelineBaseRenderer extends BaseRenderer {
	animateBubblePath = false; // used in render function
	animationDuration = 300;

	abstract axisForOverallConversion: 'xAxis' | 'yAxis';

	// to make the line appear more "curvy", we add extra "fake" points inbetween
	// example below -- fake0 has a value of REAL0, fake1 has value of REAL1 etc
	// REAL0 -- fake0 -- fake1 -- REAL1 -- fake1 -- fake2 -- REAL2
	pointsIncludingFakePerActualData = 2;

	override get visualization(): VisualizationFunnel | VisualizationPipeline {
		return super.visualization as VisualizationFunnel | VisualizationPipeline;
	}

	get series(): DChartSeries[] {
		return this.visualization.series;
	}

	get shownMainSeriesCount() {
		return this.series.filter((s) => s.isMain).length;
	}

	get firstSliceConversion(): boolean {
		return this.visualization.firstSliceConversion;
	}

	get hideStepConversion(): boolean {
		return this.visualization.hideStepConversion;
	}

	get hideOverallConversionRate(): boolean {
		return this.visualization.hideOverallConversionRate;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	override get dataOptions(): Highcharts.Options {
		const options: FunnelOrPipelineOptions = {
			series: [],
			xAxis: [
				{
					labels: { enabled: false },
					plotBands: [],
					title: {},
				},
			],
		};

		this.series.forEach((series) => {
			// get all needed points (also those with null value)
			const pointsData = this.getPointsDataForSeries(series);

			if (series.isMain) {
				this.addStepConversionsToPoints(pointsData);
			}

			const sData = this.prepareSeriesAndPointsDataForRender(series, pointsData);

			options.series.push(sData);
		});

		const { plotBands, plotLines } = this.preparePlotLinesAndBands();

		options.xAxis[0]!.plotBands = plotBands;
		options.xAxis[0]!.plotLines = plotLines;

		if (!options[this.axisForOverallConversion]?.[0]) {
			options[this.axisForOverallConversion] = [{}];
		}

		options[this.axisForOverallConversion]![0]!.title = {
			align: 'middle',
			text: this.visualization.overallConversionHtml,
			useHTML: true,
		};

		if (!options.legend) {
			options.legend = {};
		}
		options.legend.enabled = this.hasLegend;

		return options;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;
		const defs: Record<string, DChartInterface.Def> = {};

		this.seriesGradientDefs.forEach((def) => {
			defs[def.id] = def;
		});

		options = this.mergeOptions(options, { defs });

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		// const m = this.dChart.sizeModifier;

		options = this.mergeOptions(options, {
			legend: this.defaultLegendOptions,
		});

		return options;
	}

	get seriesGradientDefs(): DChartInterface.Def[] {
		return this.getSeriesGradientDefs();
	}

	override getTooltipForPoint(point: DChartPrimaryPoint) {
		if (!point.hasValue && !point.hasCompare) {
			return { html: '', typeClass: '' };
		}
		return super.getTooltipForPoint(point);
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();

		let options = this.themeOptions;

		// merge with data options since they set colorIndex
		options = this.mergeOptions(options, this.dataOptions);

		this.dChart.updateHChart(options);

		this.updateDefs();
	}

	protected override getDefs() {
		return this.seriesGradientDefs;
	}

	protected abstract preparePlotLinesAndBands(): {
		plotBands: DChartInterface.PlotBandOptions[];
		plotLines: Highcharts.XAxisPlotLinesOptions[];
	};
	protected abstract updateDynamicElementsOnRender(hChart: Highcharts.Chart): void;
	protected abstract getSeriesGradientDefs(): DChartInterface.Def[];

	protected get commmonBaseOptions(): Highcharts.Options {
		const that = this;

		return {
			chart: {
				events: {
					render: function (this: Highcharts.Chart, _event: Event) {
						that.updateDynamicElementsOnRender(this);
						if (that.hasLegend) {
							debounce(that, that.centerLegendItems, this.legend, 500);
						}
					},
				},
			},
			legend: {
				enabled: this.hasLegend,
			},
			plotOptions: {
				series: {
					animation: {
						duration: this.dChart.isAnimated ? 1000 : 0, // default series animation 1000
					},
					stickyTracking: true,
					marker: {
						enabled: false,
						states: {
							hover: {
								enabled: false,
							},
						},
					},
				},
			},
		};
	}

	protected formatPlotBandText(point: DChartPrimaryPoint): string {
		const pointIndex = point.index;
		return this.visualization.getStepLabelHtml(pointIndex);
	}

	protected getPointsDataForSeries(series: DChartSeries): DChartInterface.ChartDataPoint[] {
		// fill in all existing points
		const pointsData: DChartInterface.ChartDataPoint[] = series.points.map((point) => {
			return {
				y: point.value || 0, // missing to 0
				colorIndex: series.colorIndexOrClass,
				dataLabels: {},
				dbxPoint: point,
				events: {
					click: function (this: Highcharts.Point, e: PointerEvent) {
						const svgEl = e.target as SVGElement;
						if (svgEl.classList.contains('highcharts-dbx-percent-bubble')) {
							return;
						}
					},
				},
			};
		});

		return pointsData;
	}

	protected sendEventForBlockContentClickFromPosition(pos: number) {
		// get the actual plotBand number corresponding closest to the point clicked
		// e.g. for pointsIncludingFakePerActualData = 2:
		// - pos between 0 and 1.49 (pos 2 is second point gridline) must resolve to band 0
		// - pos between 1.5 and 3.49 (pos 4 is third point gridline) must resolve to band 1
		const roundedPos = Math.round(pos),
			lastStepIndex = this.visualization.stepCount - 1,
			actualBand = Math.min(
				lastStepIndex,
				Math.floor(roundedPos / this.pointsIncludingFakePerActualData),
			);

		let dsId;
		this.series.some((s) => {
			const point = s.points.find((p) => p.index === actualBand);
			if (point?.metricSelectionData) {
				dsId = point?.metricSelectionData.dsId;
				return true;
			}
			return false;
		});

		if (dsId) {
			this.sendEventForBlockContentClick({ dsId });
		}
	}

	private prepareSeriesAndPointsDataForRender(
		series: DChartSeries,
		pointsData: DChartInterface.ChartDataPoint[],
	): FunnelOrPipelineSeriesOptions {
		const pointsDataWithFakes = this.addFakesToPointsData(pointsData);

		const sData: FunnelOrPipelineSeriesOptions = {
			title: series.title,
			className: `type-${series.type}`,
			data: pointsDataWithFakes,
			colorIndex: series.colorIndexOrClass,
			dbxSeries: series,
		};

		if (this.shownMainSeriesCount < 2) {
			// for single main, we don't want anything to stack
			// we need the compares to be seperate and overlapping the main
			sData.stack = `random-${Math.random()}`;
		}

		return sData;
	}

	private addFakesToPointsData(
		pointsData: DChartInterface.ChartDataPoint[],
	): DChartInterface.ChartDataPoint[] {
		// adding fakes inbetween -  see pointsIncludingFakePerActualData description
		const pointsDataWithFakes: DChartInterface.ChartDataPoint[] = [];

		pointsData.forEach((pointData, idx) => {
			// creating a fake point
			const fakePoint = Object.assign({}, pointData);
			delete fakePoint.dataLabels;

			// adding the point and fakes
			pointsDataWithFakes.push(pointData, fakePoint);

			// add extra point for last point of series, to have proportional plot bands
			const isLast = idx === pointsData.length - 1;
			if (isLast) {
				pointsDataWithFakes.push(fakePoint);
			}
		});

		return pointsDataWithFakes;
	}

	private addStepConversionsToPoints(pointsData: DChartInterface.ChartDataPoint[]): void {
		// get all points to calculate step conversions
		// if we show total for all series, we get points from all series
		const pointsForData = pointsData.map((p) => p.dbxPoint);

		const seriesValues = pointsForData.map((p) => p.value),
			stepConversions = this.visualization.calculateStepConversions(seriesValues),
			stepCount = this.visualization.stepCount;

		if (!this.hideStepConversion) {
			pointsData.forEach((point, idx) => {
				// not showing bubble on last bcos its "faked"
				// not showing bubble on first with 'Step by step' conversion
				if (idx !== stepCount && (this.firstSliceConversion || idx > 0)) {
					const percent = stepConversions[idx];
					if (typeof percent === 'number') {
						point.dataLabels = {
							enabled: true,
							formatter: () => {
								return `<div class="highcharts-dbx-percent-bubble">${percent}%</div>`;
							},
							useHTML: true,
							padding: 0,
						};
					}
				}
			});
		}
	}
}
